import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useRealtimeMessage } from '../hooks/useMessageUpdates';
import InteractiveMap from '../components/InteractiveMap';
import LiveIndicator from '../components/LiveIndicator';

const MessageDetails: React.FC = () => {
  const { messageId } = useParams<{ messageId: string }>();
  const { message, loading, error, isStreaming } = useRealtimeMessage(messageId || '');
  const [copied, setCopied] = useState(false);

  const handleCopyMessageId = async () => {
    if (message) {
      try {
        await navigator.clipboard.writeText(message.id);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy message ID:', err);
      }
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading message details...</p>
        </div>
      </div>
    );
  }

  if (error || !message) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Message not found</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-6">{error || 'This message does not exist or you do not have permission to view it.'}</p>
          <Link 
            to="/messages" 
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors"
          >
            Back to Messages
          </Link>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/20';
      case 'in_transit': return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20';
      case 'pending': return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20';
      case 'failed': return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/20';
      default: return 'text-gray-600 dark:text-gray-300 bg-gray-100 dark:text-gray-400 dark:bg-gray-700';
    }
  };

  const formatStatus = (status: string) => {
    return status.replace('_', ' ').toUpperCase();
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <Link
          to="/messages"
          className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Messages
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Message Details</h1>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(message.status)}`}>
                {formatStatus(message.status)}
              </span>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">
                  Message ID: {message.id}
                </span>
                <button
                  onClick={handleCopyMessageId}
                  className="inline-flex items-center p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                  title="Copy message ID"
                >
                  {copied ? (
                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  )}
                </button>
              </div>
              {(message.status === 'pending' || message.status === 'in_transit') && (
                <LiveIndicator isStreaming={isStreaming} />
              )}
            </div>
            <div className="text-right">
              <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">${message.cost.toFixed(2)}</p>
              <p className="text-sm text-gray-500">Total Cost</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Message Content (Hidden for security) */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">Message Content</h3>
            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
              <p className="text-gray-600 dark:text-gray-300 text-sm italic">
                🔒 Message content is hidden for security purposes
              </p>
            </div>
          </div>

          {/* Delivery Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">Origin</h3>
              <div className="space-y-1">
                <p className="text-sm text-gray-900 dark:text-gray-100">{message.origin.address}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {message.origin.city}, {message.origin.country}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {message.origin.latitude.toFixed(4)}, {message.origin.longitude.toFixed(4)}
                </p>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">Destination</h3>
              <div className="space-y-1">
                <p className="text-sm text-gray-900 dark:text-gray-100">{message.destination.address}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {message.destination.city}, {message.destination.country}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {message.destination.latitude.toFixed(4)}, {message.destination.longitude.toFixed(4)}
                </p>
              </div>
            </div>
          </div>

          {/* Bird Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">Messenger Bird</h3>
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="text-2xl">🦅</div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">{message.bird_type.name}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300 italic mb-2">{message.bird_type.species}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">{message.bird_type.description}</p>
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Speed:</span>
                      <span className="ml-1 font-medium">{message.bird_type.properties.speed} km/h</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Max Range:</span>
                      <span className="ml-1 font-medium">{message.bird_type.properties.maxDistance}km</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Reliability:</span>
                      <span className="ml-1 font-medium">{message.bird_type.properties.reliability}%</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Max Message:</span>
                      <span className="ml-1 font-medium">{message.bird_type.properties.maxMessageLength} chars</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Timing Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Created</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {new Date(message.created_at).toLocaleString()}
              </p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Estimated Delivery</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {new Date(message.estimated_delivery).toLocaleString()}
              </p>
            </div>
            {message.actual_delivery && (
              <div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Actual Delivery</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {new Date(message.actual_delivery).toLocaleString()}
                </p>
              </div>
            )}
          </div>

          {/* Progress */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Delivery Progress</h3>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {message.progress}% Complete
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${message.progress}%` }}
              ></div>
            </div>
            {message.current_location && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Last known location: {message.current_location.address}
              </p>
            )}
          </div>

          {/* Interactive Map */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Flight Path Tracking</h3>
            <InteractiveMap
              origin={message.origin}
              destination={message.destination}
              currentLocation={message.current_location}
              className="w-full h-96"
              showFlightPath={true}
              fitBounds={true}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              🦅 Live bird tracking • 📍 Origin • 🎯 Destination
            </p>
          </div>

          {/* Tracking Events */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Tracking History</h3>
            <div className="space-y-3">
              {message.tracking_events.map((event, index) => (
                <div key={event.id} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1">
                    <div className={`w-2 h-2 rounded-full ${
                      index === 0 ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                    }`}></div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {event.event_type.replace('_', ' ')}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(event.timestamp).toLocaleString()}
                      </p>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{event.description}</p>
                    {event.location && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        {event.location.address || `${event.location.latitude.toFixed(4)}, ${event.location.longitude.toFixed(4)}`}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageDetails;
